import { useFlag } from '@automata/utils/hooks';
import {
  useGetAllTransportConfigs,
  useGetTransportConfigVersions,
} from '@automata/wfb-api';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import {
  accordionDetailsSx,
  accordionSummarySx,
  accordionSx,
  expandIconSx,
} from './styles/workcellFormStyles';

type TransportConfigType = 'V1' | 'V3';

type Props = {
  changed: boolean;
  defaultTransportConfigId?: string;
  selectedTransportConfigId?: string;
  selectedTransportConfigType?: TransportConfigType;
  selectedTransportConfigVersion?: number;
  onChange: (value: {
    id?: string;
    type?: TransportConfigType;
    version?: number;
  }) => void;
};

const labelSx = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
};

const formatConfigLabel = (
  name: string,
  type: TransportConfigType,
  isDefault: boolean
) => `${name} (${type})${isDefault ? ' (Default)' : ''}`;

const extractVersionNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  if (Array.isArray(value)) {
    return extractVersionNumber(value[0]);
  }

  if (value != null && typeof value === 'object') {
    const candidate = (value as { version?: unknown }).version;
    return extractVersionNumber(candidate);
  }

  return undefined;
};

const LATEST_VERSION_VALUE = 'latest';

export const TransportConfigurationSection = ({
  changed,
  defaultTransportConfigId,
  selectedTransportConfigId,
  selectedTransportConfigType,
  selectedTransportConfigVersion,
  onChange,
}: Props) => {
  const [expanded, setExpanded] = useState(true);
  const { data: allConfigs = [] } = useGetAllTransportConfigs({});

  const isTransportConfigHandlingEnabled = useFlag(
    'fe.transport_config_handling'
  );

  const transportConfigOptions = useMemo(() => {
    const optionsByKey = new Map<string, (typeof allConfigs)[number]>();

    allConfigs.forEach((config) => {
      const key = `${config.id}:${config.type}`;
      const existing = optionsByKey.get(key);

      // Keep the highest known version per config ID/type pair.
      if (!existing || config.version > existing.version) {
        optionsByKey.set(key, config);
      }
    });

    return Array.from(optionsByKey.values()).sort((left, right) => {
      const nameCompare = left.name.localeCompare(right.name);
      if (nameCompare !== 0) {
        return nameCompare;
      }
      return left.type.localeCompare(right.type);
    });
  }, [allConfigs]);

  const selectedConfigMeta = useMemo(() => {
    if (!selectedTransportConfigId) {
      return undefined;
    }

    if (selectedTransportConfigType) {
      return transportConfigOptions.find(
        (config) =>
          config.id === selectedTransportConfigId &&
          config.type === selectedTransportConfigType
      );
    }

    return transportConfigOptions.find(
      (config) => config.id === selectedTransportConfigId
    );
  }, [
    selectedTransportConfigId,
    selectedTransportConfigType,
    transportConfigOptions,
  ]);

  const resolvedTransportConfigId =
    selectedTransportConfigId ?? selectedConfigMeta?.id;
  const resolvedTransportConfigType =
    selectedTransportConfigType ?? selectedConfigMeta?.type;

  useEffect(() => {
    if (
      !selectedTransportConfigId ||
      selectedTransportConfigType ||
      !selectedConfigMeta
    ) {
      return;
    }

    onChange({
      id: selectedTransportConfigId,
      type: selectedConfigMeta.type,
      version: selectedTransportConfigVersion,
    });
  }, [
    onChange,
    selectedConfigMeta,
    selectedTransportConfigId,
    selectedTransportConfigType,
    selectedTransportConfigVersion,
  ]);

  const { data: transportConfigVersions = [] } = useGetTransportConfigVersions(
    {
      queryParams: {
        id: resolvedTransportConfigId ?? '',
        type: resolvedTransportConfigType ?? 'V1',
      },
    },
    {
      enabled: Boolean(
        resolvedTransportConfigId && resolvedTransportConfigType
      ),
    }
  );

  const sortedVersions = useMemo(() => {
    const normalizedVersions = (transportConfigVersions as unknown[])
      .map(extractVersionNumber)
      .filter((version): version is number => version !== undefined);

    return Array.from(new Set(normalizedVersions)).sort(
      (left, right) => right - left
    );
  }, [transportConfigVersions]);

  const latestVersion = sortedVersions[0];
  const selectedConfigKey =
    resolvedTransportConfigId && resolvedTransportConfigType
      ? `${resolvedTransportConfigId}:${resolvedTransportConfigType}`
      : undefined;

  useEffect(() => {
    if (
      !selectedConfigKey ||
      !resolvedTransportConfigId ||
      !resolvedTransportConfigType ||
      latestVersion === undefined
    ) {
      return;
    }

    const hasValidSelectedVersion =
      selectedTransportConfigVersion === undefined ||
      sortedVersions.includes(selectedTransportConfigVersion);

    if (!hasValidSelectedVersion) {
      onChange({
        id: resolvedTransportConfigId,
        type: resolvedTransportConfigType,
        version: undefined,
      });
    }
  }, [
    latestVersion,
    onChange,
    resolvedTransportConfigId,
    resolvedTransportConfigType,
    selectedConfigKey,
    selectedTransportConfigVersion,
    sortedVersions,
  ]);

  const selectedConfigValue =
    resolvedTransportConfigId && resolvedTransportConfigType
      ? `${resolvedTransportConfigId}:${resolvedTransportConfigType}`
      : '';

  const selectedVersionValue =
    selectedTransportConfigVersion !== undefined
      ? String(selectedTransportConfigVersion)
      : LATEST_VERSION_VALUE;

  const handleConfigChange = (event: SelectChangeEvent<string>) => {
    const nextValue = event.target.value;

    if (!nextValue) {
      onChange({});
      return;
    }

    const [nextId, nextType] = nextValue.split(':') as [
      string,
      TransportConfigType
    ];

    onChange({
      id: nextId,
      type: nextType,
      version: undefined,
    });
  };

  const handleVersionChange = (event: SelectChangeEvent<string>) => {
    if (!resolvedTransportConfigId || !resolvedTransportConfigType) {
      return;
    }

    const value = event.target.value;
    const nextVersion =
      value === LATEST_VERSION_VALUE ? undefined : Number(value);

    if (nextVersion !== undefined && Number.isNaN(nextVersion)) {
      return;
    }

    onChange({
      id: resolvedTransportConfigId,
      type: resolvedTransportConfigType,
      version: nextVersion,
    });
  };

  if (!isTransportConfigHandlingEnabled) return;

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded((previous) => !previous)}
      sx={accordionSx}
    >
      <AccordionSummary sx={accordionSummarySx}>
        <ExpandMoreIcon sx={expandIconSx(expanded)} />
        <Typography variant="subtitle1" fontWeight={600} sx={{ width: '100%' }}>
          <Stack direction="row" justifyContent="space-between" mr="73px">
            <Box sx={{ m: '0 8px' }}></Box>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{ width: '100%' }}
            >
              Transport Configuration
            </Typography>
            {changed && (
              <Typography color="error" fontSize={14} sx={{ ml: 1 }}>
                ●
              </Typography>
            )}
          </Stack>
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ ...accordionDetailsSx, pr: 3 }}>
        <Stack gap={2.5} maxWidth="75%">
          <FormControl size="small" fullWidth>
            <Typography sx={labelSx}>Configuration in use</Typography>
            <Select
              displayEmpty
              value={selectedConfigValue}
              onChange={handleConfigChange}
            >
              <MenuItem value="" disabled>
                <em>Select a configuration</em>
              </MenuItem>
              {transportConfigOptions.map((config) => (
                <MenuItem
                  key={`${config.id}:${config.type}`}
                  value={`${config.id}:${config.type}`}
                >
                  {formatConfigLabel(
                    config.name,
                    config.type,
                    config.id === defaultTransportConfigId
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            size="small"
            fullWidth
            disabled={
              !resolvedTransportConfigId ||
              !resolvedTransportConfigType ||
              sortedVersions.length === 0
            }
            sx={{ mb: '8px' }}
          >
            <Typography sx={labelSx}>Version</Typography>
            <Select value={selectedVersionValue} onChange={handleVersionChange}>
              {latestVersion !== undefined ? (
                <MenuItem value={LATEST_VERSION_VALUE}>
                  {`Latest (Version ${latestVersion})`}
                </MenuItem>
              ) : (
                <MenuItem value="" disabled>
                  No versions available
                </MenuItem>
              )}
              {sortedVersions.map((version) => (
                <MenuItem key={version} value={String(version)}>
                  {`Version ${version}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
